import { useState, useEffect, useCallback } from 'react';

// The version this app was built with (embedded at build time)
const CURRENT_VERSION = '__APP_VERSION__';

// How often to check for updates (in milliseconds)
const CHECK_INTERVAL = 30 * 1000; // 30 seconds

/**
 * Hook to check for app updates and manage the update process
 *
 * Features:
 * - Polls version.json every 30 seconds
 * - Detects when a new version is available
 * - Supports force update for critical releases
 * - Provides one-click update that clears all caches
 */
export function useAppUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [newVersion, setNewVersion] = useState(null);
  const [releaseNotes, setReleaseNotes] = useState('');
  const [forceUpdate, setForceUpdate] = useState(false);
  const [checking, setChecking] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Get the base path for the app
  const getBasePath = () => {
    if (window.location.pathname.startsWith('/tracker')) {
      return '/tracker';
    }
    return '';
  };

  // Check for updates
  const checkForUpdate = useCallback(async () => {
    if (checking) return;

    setChecking(true);
    try {
      // Add cache-busting timestamp to bypass browser cache
      const timestamp = Date.now();
      const basePath = getBasePath();
      const response = await fetch(`${basePath}/version.json?t=${timestamp}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        console.warn('Failed to fetch version.json:', response.status);
        return;
      }

      const serverVersion = await response.json();

      // Compare versions
      const currentVersion = localStorage.getItem('app_version') || CURRENT_VERSION;

      if (serverVersion.version !== currentVersion && serverVersion.version !== '__APP_VERSION__') {
        console.log(`Update available: ${currentVersion} -> ${serverVersion.version}`);
        setUpdateAvailable(true);
        setNewVersion(serverVersion.version);
        setReleaseNotes(serverVersion.releaseNotes || '');
        setForceUpdate(serverVersion.forceUpdate === true);

        // If force update is enabled, auto-update immediately
        if (serverVersion.forceUpdate === true) {
          console.log('Force update enabled, updating automatically...');
          setTimeout(() => performUpdate(), 2000);
        }
      }
    } catch (err) {
      console.warn('Error checking for updates:', err);
    } finally {
      setChecking(false);
    }
  }, [checking]);

  // Perform the update - clear all caches and reload
  const performUpdate = useCallback(async () => {
    setUpdating(true);

    // Save the new version to localStorage first (so we don't prompt again after reload)
    if (newVersion) {
      localStorage.setItem('app_version', newVersion);
    }

    // Preserve auth tokens before clearing storage
    const backendToken = sessionStorage.getItem('msal.backendToken');
    const msalKeys = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('msal.')) {
        msalKeys.push({ key, value: sessionStorage.getItem(key) });
      }
    }

    // Helper to force reload after timeout (fallback)
    const forceReload = () => {
      // Clear and restore auth
      sessionStorage.clear();
      if (backendToken) {
        sessionStorage.setItem('msal.backendToken', backendToken);
      }
      msalKeys.forEach(({ key, value }) => {
        if (value) sessionStorage.setItem(key, value);
      });

      // Force reload - use href assignment for more reliable reload
      console.log('Force reloading app...');
      window.location.href = window.location.href.split('?')[0] + '?v=' + Date.now();
    };

    // Set a hard timeout - if update takes more than 3 seconds, just reload
    const timeoutId = setTimeout(() => {
      console.warn('Update taking too long, forcing reload...');
      forceReload();
    }, 3000);

    try {
      // 1. Unregister all service workers (don't await - fire and forget)
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(registration => {
            registration.unregister().catch(() => {});
          });
        }).catch(() => {});
      }

      // 2. Clear all caches (don't await - fire and forget)
      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          cacheNames.forEach(cacheName => {
            caches.delete(cacheName).catch(() => {});
          });
        }).catch(() => {});
      }

      // 3. Small delay to let cache clearing start, then reload
      await new Promise(resolve => setTimeout(resolve, 500));

      clearTimeout(timeoutId);
      forceReload();

    } catch (err) {
      console.error('Error during update:', err);
      clearTimeout(timeoutId);
      forceReload();
    }
  }, [newVersion]);

  // Dismiss the update banner (for non-force updates)
  const dismissUpdate = useCallback(() => {
    if (!forceUpdate) {
      setUpdateAvailable(false);
      // Don't check again for 5 minutes after dismissing
      setTimeout(() => {
        checkForUpdate();
      }, 5 * 60 * 1000);
    }
  }, [forceUpdate, checkForUpdate]);

  // Set up polling for updates
  useEffect(() => {
    // Check immediately on mount
    checkForUpdate();

    // Then check periodically
    const interval = setInterval(checkForUpdate, CHECK_INTERVAL);

    // Also check when the page becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForUpdate]);

  return {
    updateAvailable,
    newVersion,
    releaseNotes,
    forceUpdate,
    checking,
    updating,
    performUpdate,
    dismissUpdate,
    checkForUpdate,
    currentVersion: localStorage.getItem('app_version') || CURRENT_VERSION,
  };
}

export default useAppUpdate;
